import { LocalStorageKeys, ResourceName } from "@/utils";
import { Close, Download, Search } from "@mui/icons-material";
import {
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { useIsFetching } from "@tanstack/react-query";
import { FC } from "react";
import { TopToolbar as RaTopToolbar, useListContext } from "react-admin";
import { useUIStore } from "@/stores";
import { CreateAffiliate } from "../../modals";
import { useDebouncedListSearch, useDownloadFile } from "@/hooks";
import { SortDirection } from "@/api/shared.ts";
import { AffiliateApi } from "@/api/affiliates";

const TopToolbar: FC = () => {
  const { filterValues, sort, setSort } = useListContext();
  const { openModal } = useUIStore();

  const { isLoading: isCsvDownloading, download: downloadCsv } =
    useDownloadFile({
      apiFunc: AffiliateApi.downloadCsv,
      localStorageFileKey: LocalStorageKeys.affiliateUsersCsvReport,
      filterValues,
      sort: {
        sortBy: sort.field,
        sortDirection: sort.order.toLowerCase() as SortDirection,
      },
    });

  const isUsersFetching = useIsFetching({
    queryKey: [ResourceName.AFFILIATE_USERS, "getList"],
  });

  const [searchQuery, setSearchQuery] = useDebouncedListSearch();

  return (
    <RaTopToolbar
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        marginTop: 1,
        paddingRight: 0,
        paddingLeft: 0,
      }}
    >
      <Stack
        direction={"row"}
        sx={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        {sort.field === "earnings" ? (
          <Chip
            size="small"
            variant="filled"
            label={"Sorted by: Earning"}
            onDelete={() => setSort({ field: "createdAt", order: "ASC" })}
          />
        ) : (
          <div />
        )}

        <Stack direction={"row"} spacing={1}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by affiliate"
            sx={{ width: "220px" }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 50 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {!!isUsersFetching && <CircularProgress size={16} />}
                  {!isUsersFetching && !!searchQuery && (
                    <Close
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSearchQuery("")}
                    />
                  )}
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              openModal(CreateAffiliate, { title: "New Affiliate" })
            }
          >
            New Affiliate
          </Button>
          <Button
            size="small"
            endIcon={<Download />}
            sx={{ minWidth: "auto" }}
            loading={isCsvDownloading}
            disabled={isCsvDownloading}
            onClick={downloadCsv}
          >
            Download CSV
          </Button>
        </Stack>
      </Stack>
    </RaTopToolbar>
  );
};

export { TopToolbar };
